import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUtils } from '../utils/api';

// Minimal step-1 editor: Home page hero section only.
// Next steps: full section list editor + global refs + reorder.

function HeroImageDropzone({ value, onChange }) {
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={
          `w-full border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ` +
          (isDragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:bg-gray-50')
        }
      >
        <input {...getInputProps()} />
        <div className="text-sm text-gray-700">
          {isDragActive ? 'Drop the image here…' : 'Drag & drop an image here, or click to select'}
        </div>
      </div>

      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Image URL"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />

      {value ? (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <img src={value} alt="Hero preview" className="w-full h-48 object-cover" />
        </div>
      ) : null}
    </div>
  );
}

function LogoDropzone({ value, onChange }) {
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div
      {...getRootProps()}
      className={
        `w-full border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ` +
        (isDragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:bg-gray-50')
      }
    >
      <input {...getInputProps()} />

      {value ? (
        <div className="w-full h-24 flex items-center justify-center bg-white rounded-lg overflow-hidden">
          <img src={value} alt="Logo preview" className="max-h-24 w-auto object-contain" />
        </div>
      ) : (
        <div className="text-sm text-gray-700">
          {isDragActive ? 'Drop the image here…' : 'Drag & drop a logo here, or click to select'}
        </div>
      )}
    </div>
  );
}

// General purpose image dropzone with URL input
function ImageDropzone({ value, onChange, label = 'Image', previewHeight = 'h-48' }) {
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={
          `w-full border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ` +
          (isDragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:bg-gray-50')
        }
      >
        <input {...getInputProps()} />
        <div className="text-sm text-gray-700">
          {isDragActive ? 'Drop the image here…' : `Drag & drop ${label.toLowerCase()} here, or click to select`}
        </div>
      </div>

      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste image URL here"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />

      {value ? (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <img src={value} alt={`${label} preview`} className={`w-full ${previewHeight} object-cover`} />
        </div>
      ) : null}
    </div>
  );
}

// Avatar dropzone with rounded preview for testimonials
function AvatarDropzone({ value, onChange, label = 'Avatar' }) {
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={
          `w-full border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ` +
          (isDragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:bg-gray-50')
        }
      >
        <input {...getInputProps()} />
        <div className="text-sm text-gray-700">
          {isDragActive ? 'Drop the avatar here…' : `Drag & drop ${label.toLowerCase()} here, or click to select`}
        </div>
      </div>

      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste avatar URL here"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />

      {value ? (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 inline-flex">
          <img 
            src={value} 
            alt={`${label} preview`} 
            className="w-16 h-16 rounded-full object-cover border-2 border-cyan-200 shadow-sm" 
          />
        </div>
      ) : null}
    </div>
  );
}

export default function HomePageEditor() {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);

  const [heroOpen, setHeroOpen] = useState(false);

  const [statsOpen, setStatsOpen] = useState(false);
  const [architectureOpen, setArchitectureOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [testimonialsOpen, setTestimonialsOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);

  const [hero, setHero] = useState({
    eyebrow: '',
    headingLine1: '',
    headingLine2: '',
    description: '',
    backgroundImage: '',
    primaryCtaText: '',
    primaryCtaLink: '',
    secondaryCtaText: '',
    secondaryCtaLink: '',
    isActive: true,
  });

  const [stats, setStats] = useState({
    eyebrow: '',
    titleHtml: '',
    items: [],
    isActive: true,
  });

  const [architecture, setArchitecture] = useState({
    titleHtml: '',
    paragraphs: [],
    features: [],
    storyLinkText: '',
    storyLinkHref: '',
    imageTopLeft: '',
    imageBottomRight: '',
    countriesValue: '',
    countriesLabel: '',
    countriesDescription: '',
    maritimeTitle: '',
    maritimeDescription: '',
    isActive: true,
  });

  const [collections, setCollections] = useState({
    titleHtml: '',
    description: '',
    items: [],
    isActive: true,
  });

  const [services, setServices] = useState({
    eyebrow: '',
    titleHtml: '',
    items: [],
    isActive: true,
  });

  const [testimonials, setTestimonials] = useState({
    title: '',
    items: [],
    isActive: true,
  });

  const [partners, setPartners] = useState({
    title: '',
    description: '',
    logos: [],
    isActive: true,
  });

  const servicesSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'services');
    return existing?.sectionId || 'services-1';
  }, [page]);

  const testimonialsSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'testimonials');
    return existing?.sectionId || 'testimonials-1';
  }, [page]);

  const partnersSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'partners');
    return existing?.sectionId || 'partners-1';
  }, [page]);



  const heroSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'hero');
    return existing?.sectionId || 'hero-1';
  }, [page]);

  const statsSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'stats');
    return existing?.sectionId || 'stats-1';
  }, [page]);

  const architectureSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'architecture');
    return existing?.sectionId || 'architecture-1';
  }, [page]);

  const collectionsSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'collections');
    return existing?.sectionId || 'collections-1';
  }, [page]);


  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiUtils.cms.getPage('home');

      setLoading(false);
      if (!res.success) {
        toast.error(res.error || 'Failed to load home page');
        // Still allow editing/saving by initializing an empty page model
        setPage({ pageName: 'home', metaTitle: '', metaDescription: '', sections: [] });
        return;
      }

      setPage(res.data);

      const heroSection = (res.data?.sections || []).find(s => s.sectionType === 'hero' && !s.isGlobal);
      const c = heroSection?.content || heroSection || {};
      setHero({
        headingLine1: c.headingLine1 || c.title || '',
        headingLine2: c.headingLine2 || c.subtitle || '',
        description: c.description || '',
        backgroundImage: c.backgroundImage || '',
        primaryCtaText: c.primaryCtaText || c.ctaText || '',
        primaryCtaLink: c.primaryCtaLink || c.ctaLink || '/products',
        secondaryCtaText: c.secondaryCtaText || '',
        secondaryCtaLink: c.secondaryCtaLink || '/contact',
        isActive: heroSection?.isActive ?? true,
      });

      const statsSection = (res.data?.sections || []).find(s => s.sectionType === 'stats' && !s.isGlobal);
      const sc = statsSection?.content || statsSection || {};
      setStats({
        eyebrow: sc.eyebrow || 'OUR IMPACT',
        titleHtml: sc.titleHtml || 'Trusted by <br /> Global Leaders',
        items: Array.isArray(sc.items) ? sc.items : [],
        isActive: statsSection?.isActive ?? true,
      });

      const archSection = (res.data?.sections || []).find(s => s.sectionType === 'architecture' && !s.isGlobal);
      const ac = archSection?.content || archSection || {};
      setArchitecture(prev => ({
        ...prev,
        titleHtml: ac.titleHtml || prev.titleHtml,
        paragraphs: Array.isArray(ac.paragraphs) && ac.paragraphs.length ? ac.paragraphs : prev.paragraphs,
        features: Array.isArray(ac.features) && ac.features.length ? ac.features : prev.features,
        storyLinkText: ac.storyLinkText || prev.storyLinkText,
        storyLinkHref: ac.storyLinkHref || prev.storyLinkHref,
        imageTopLeft: ac.imageTopLeft || prev.imageTopLeft,
        imageBottomRight: ac.imageBottomRight || prev.imageBottomRight,
        countriesValue: ac.countriesValue || prev.countriesValue,
        countriesLabel: ac.countriesLabel || prev.countriesLabel,
        countriesDescription: ac.countriesDescription || prev.countriesDescription,
        maritimeTitle: ac.maritimeTitle || prev.maritimeTitle,
        maritimeDescription: ac.maritimeDescription || prev.maritimeDescription,
        isActive: archSection?.isActive ?? true,
      }));

      const collectionsSection = (res.data?.sections || []).find(s => s.sectionType === 'collections' && !s.isGlobal);
      const cc = collectionsSection?.content || collectionsSection || {};
      setCollections(prev => ({
        ...prev,
        titleHtml: cc.titleHtml || prev.titleHtml,
        description: cc.description || prev.description,
        items: Array.isArray(cc.items) ? cc.items : prev.items,
        isActive: collectionsSection?.isActive ?? true,
      }));

      const servicesSection = (res.data?.sections || []).find(s => s.sectionType === 'services' && !s.isGlobal);
      const sv = servicesSection?.content || servicesSection || {};
      setServices(prev => ({
        ...prev,
        eyebrow: sv.eyebrow || prev.eyebrow,
        titleHtml: sv.titleHtml || prev.titleHtml,
        items: Array.isArray(sv.items) ? sv.items : prev.items,
        isActive: servicesSection?.isActive ?? true,
      }));

      const testimonialsSection = (res.data?.sections || []).find(s => s.sectionType === 'testimonials' && !s.isGlobal);
      const tv = testimonialsSection?.content || testimonialsSection || {};
      setTestimonials(prev => ({
        ...prev,
        title: tv.title || prev.title,
        items: Array.isArray(tv.items) ? tv.items : prev.items,
        isActive: testimonialsSection?.isActive ?? true,
      }));

      const partnersSection = (res.data?.sections || []).find(s => s.sectionType === 'partners' && !s.isGlobal);
      const pv = partnersSection?.content || partnersSection || {};
      setPartners(prev => ({
        ...prev,
        title: pv.title || prev.title,
        description: pv.description || prev.description,
        logos: Array.isArray(pv.logos) ? pv.logos : prev.logos,
        isActive: partnersSection?.isActive ?? true,
      }));

    })();
  }, []);

  const onChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setHero(prev => ({ ...prev, [key]: value }));
  };

  const onStatsChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setStats(prev => ({ ...prev, [key]: value }));
  };

  const onArchChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setArchitecture(prev => ({ ...prev, [key]: value }));
  };

  const onCollectionsChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCollections(prev => ({ ...prev, [key]: value }));
  };

  const onServicesChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setServices(prev => ({ ...prev, [key]: value }));
  };

  const onTestimonialsChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setTestimonials(prev => ({ ...prev, [key]: value }));
  };

  const onPartnersChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPartners(prev => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    const safePage = page || { metaTitle: '', metaDescription: '', sections: [] };

    setLoading(true);
    try {
      // Build new sections array: upsert hero section, keep others unchanged.
      const existingSections = safePage.sections || [];
      const existingHeroIndex = existingSections.findIndex(s => !s.isGlobal && s.sectionType === 'hero');

      const nextHeroSection = {
        sectionId: heroSectionId,
        sectionType: 'hero',
        isGlobal: false,
        order: existingHeroIndex >= 0 ? existingSections[existingHeroIndex].order : 0,
        isActive: !!hero.isActive,
        content: {
          eyebrow: hero.eyebrow,
          headingLine1: hero.headingLine1,
          headingLine2: hero.headingLine2,
          description: hero.description,
          backgroundImage: hero.backgroundImage,
          primaryCtaText: hero.primaryCtaText,
          primaryCtaLink: hero.primaryCtaLink,
          secondaryCtaText: hero.secondaryCtaText,
          secondaryCtaLink: hero.secondaryCtaLink,
        },
        visualSettings: {},
      };

      const upsert = (arr, predicate, section) => {
        const idx = arr.findIndex(predicate);
        if (idx >= 0) return arr.map((s, i) => (i === idx ? section : s));
        return [section, ...arr];
      };

      let nextSections = upsert(existingSections, (s) => !s.isGlobal && s.sectionType === 'hero', nextHeroSection);

      const existingStatsIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'stats');
      const nextStatsSection = {
        sectionId: statsSectionId,
        sectionType: 'stats',
        isGlobal: false,
        order: existingStatsIndex >= 0 ? nextSections[existingStatsIndex].order : 1,
        isActive: !!stats.isActive,
        content: {
          eyebrow: stats.eyebrow,
          titleHtml: stats.titleHtml,
          items: stats.items,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'stats', nextStatsSection);

      const existingArchIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'architecture');
      const nextArchSection = {
        sectionId: architectureSectionId,
        sectionType: 'architecture',
        isGlobal: false,
        order: existingArchIndex >= 0 ? nextSections[existingArchIndex].order : 2,
        isActive: !!architecture.isActive,
        content: {
          titleHtml: architecture.titleHtml,
          paragraphs: architecture.paragraphs,
          features: architecture.features,
          storyLinkText: architecture.storyLinkText,
          storyLinkHref: architecture.storyLinkHref,
          imageTopLeft: architecture.imageTopLeft,
          imageBottomRight: architecture.imageBottomRight,
          countriesValue: architecture.countriesValue,
          countriesLabel: architecture.countriesLabel,
          countriesDescription: architecture.countriesDescription,
          maritimeTitle: architecture.maritimeTitle,
          maritimeDescription: architecture.maritimeDescription,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'architecture', nextArchSection);

      const existingCollectionsIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'collections');
      const nextCollectionsSection = {
        sectionId: collectionsSectionId,
        sectionType: 'collections',
        isGlobal: false,
        order: existingCollectionsIndex >= 0 ? nextSections[existingCollectionsIndex].order : 3,
        isActive: !!collections.isActive,
        content: {
          titleHtml: collections.titleHtml,
          description: collections.description,
          items: collections.items,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'collections', nextCollectionsSection);

      const existingServicesIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'services');
      const nextServicesSection = {
        sectionId: servicesSectionId,
        sectionType: 'services',
        isGlobal: false,
        order: existingServicesIndex >= 0 ? nextSections[existingServicesIndex].order : 4,
        isActive: !!services.isActive,
        content: {
          eyebrow: services.eyebrow,
          titleHtml: services.titleHtml,
          items: services.items,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'services', nextServicesSection);

      const existingTestimonialsIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'testimonials');
      const nextTestimonialsSection = {
        sectionId: testimonialsSectionId,
        sectionType: 'testimonials',
        isGlobal: false,
        order: existingTestimonialsIndex >= 0 ? nextSections[existingTestimonialsIndex].order : 5,
        isActive: !!testimonials.isActive,
        content: {
          title: testimonials.title,
          items: testimonials.items,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'testimonials', nextTestimonialsSection);

      const existingPartnersIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'partners');
      const nextPartnersSection = {
        sectionId: partnersSectionId,
        sectionType: 'partners',
        isGlobal: false,
        order: existingPartnersIndex >= 0 ? nextSections[existingPartnersIndex].order : 6,
        isActive: !!partners.isActive,
        content: {
          title: partners.title,
          description: partners.description,
          logos: partners.logos,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'partners', nextPartnersSection);

      // Normalize sections to match backend validation exactly.
      const normalizedSections = nextSections
        .slice()
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((s, idx) => {
          const isGlobal = !!s.isGlobal;
          const base = {
            sectionId: String(s.sectionId),
            order: idx,
            isActive: s.isActive !== false,
            isGlobal,
          };

          if (isGlobal) {
            return {
              ...base,
              globalSectionRef: String(s.globalSectionRef || '').toLowerCase(),
            };
          }

          return {
            ...base,
            sectionType: String(s.sectionType || '').toLowerCase(),
            content: s.content || {},
            visualSettings: s.visualSettings || {},
          };
        })
        // Drop invalid global refs to avoid 400s.
        .filter((s) => (s.isGlobal ? !!s.globalSectionRef : !!s.sectionType));

      const res = await apiUtils.cms.updatePage('home', {
        metaTitle: safePage.metaTitle || '',
        metaDescription: safePage.metaDescription || '',
        sections: normalizedSections,
      });

      if (!res.success) {
        toast.error(res.error || 'Failed to save');
        return;
      }

      setPage(res.data);
      toast.success('Home page saved');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Home</h1>
        </div>
        <button
          onClick={onSave}
          disabled={loading}
          className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setHeroOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={heroOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Hero</div>
              <div className="text-xs text-gray-500">sectionId: <code>{heroSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${heroOpen ? 'rotate-180' : ''}`} />
          </button>

          {heroOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit fields below</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!hero.isActive} onChange={onChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Background Image</label>
                  <HeroImageDropzone
                    value={hero.backgroundImage}
                    onChange={(url) => setHero(prev => ({ ...prev, backgroundImage: url }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload an image (stored as a data URL for now).</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                  <input value={hero.eyebrow} onChange={onChange('eyebrow')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., International Trade Solutions" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading line 1</label>
                  <input value={hero.headingLine1} onChange={onChange('headingLine1')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading line 2</label>
                  <input value={hero.headingLine2} onChange={onChange('headingLine2')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={hero.description} onChange={onChange('description')} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary CTA text</label>
                  <input value={hero.primaryCtaText} onChange={onChange('primaryCtaText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary CTA link</label>
                  <input value={hero.primaryCtaLink} onChange={onChange('primaryCtaLink')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary CTA text</label>
                  <input value={hero.secondaryCtaText} onChange={onChange('secondaryCtaText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary CTA link</label>
                  <input value={hero.secondaryCtaLink} onChange={onChange('secondaryCtaLink')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Impact / Stats Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setStatsOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={statsOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Impact (Stats)</div>
              <div className="text-xs text-gray-500">sectionId: <code>{statsSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${statsOpen ? 'rotate-180' : ''}`} />
          </button>

          {statsOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit heading and stat cards</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!stats.isActive} onChange={onStatsChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow</label>
                  <input value={stats.eyebrow} onChange={onStatsChange('eyebrow')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (HTML)</label>
                  <input value={stats.titleHtml} onChange={onStatsChange('titleHtml')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">Use <code>&lt;br /&gt;</code> for line break.</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Stat Cards</div>
                <button
                  type="button"
                  onClick={() => setStats(prev => ({
                    ...prev,
                    items: [...prev.items, { value: '', label: '', description: '' }],
                  }))}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Add Card
                </button>
              </div>

              <div className="space-y-3">
                {(stats.items || []).map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">Card #{idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => setStats(prev => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== idx),
                        }))}
                        className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                        <input
                          value={item.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setStats(prev => ({
                              ...prev,
                              items: prev.items.map((it, i) => i === idx ? { ...it, value } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                        <input
                          value={item.label || ''}
                          onChange={(e) => {
                            const label = e.target.value;
                            setStats(prev => ({
                              ...prev,
                              items: prev.items.map((it, i) => i === idx ? { ...it, label } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => {
                            const description = e.target.value;
                            setStats(prev => ({
                              ...prev,
                              items: prev.items.map((it, i) => i === idx ? { ...it, description } : it),
                            }));
                          }}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(stats.items || []).length === 0 && (
                  <div className="text-sm text-gray-500">No cards yet. Click “Add Card”.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Architecture Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setArchitectureOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={architectureOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Architecture</div>
              <div className="text-xs text-gray-500">sectionId: <code>{architectureSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${architectureOpen ? 'rotate-180' : ''}`} />
          </button>

          {architectureOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit left content + right cards/images</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!architecture.isActive} onChange={onArchChange('isActive')} />
                  Active
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (HTML)</label>
                <input value={architecture.titleHtml} onChange={onArchChange('titleHtml')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paragraph 1</label>
                  <textarea
                    value={architecture.paragraphs?.[0] || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        paragraphs: [v, prev.paragraphs?.[1] || ''],
                      }));
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paragraph 2</label>
                  <textarea
                    value={architecture.paragraphs?.[1] || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        paragraphs: [prev.paragraphs?.[0] || '', v],
                      }));
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Feature 1</div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    value={architecture.features?.[0]?.title || ''}
                    onChange={(e) => {
                      const title = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        features: [
                          { ...(prev.features?.[0] || {}), title },
                          prev.features?.[1] || { title: '', description: '' },
                        ],
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={architecture.features?.[0]?.description || ''}
                    onChange={(e) => {
                      const description = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        features: [
                          { ...(prev.features?.[0] || {}), description },
                          prev.features?.[1] || { title: '', description: '' },
                        ],
                      }));
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Feature 2</div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    value={architecture.features?.[1]?.title || ''}
                    onChange={(e) => {
                      const title = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        features: [
                          prev.features?.[0] || { title: '', description: '' },
                          { ...(prev.features?.[1] || {}), title },
                        ],
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={architecture.features?.[1]?.description || ''}
                    onChange={(e) => {
                      const description = e.target.value;
                      setArchitecture(prev => ({
                        ...prev,
                        features: [
                          prev.features?.[0] || { title: '', description: '' },
                          { ...(prev.features?.[1] || {}), description },
                        ],
                      }));
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Story link text</label>
                  <input value={architecture.storyLinkText} onChange={onArchChange('storyLinkText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Story link href</label>
                  <input value={architecture.storyLinkHref} onChange={onArchChange('storyLinkHref')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Top-left image</label>
                  <HeroImageDropzone
                    value={architecture.imageTopLeft}
                    onChange={(url) => setArchitecture(prev => ({ ...prev, imageTopLeft: url }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bottom-right image</label>
                  <HeroImageDropzone
                    value={architecture.imageBottomRight}
                    onChange={(url) => setArchitecture(prev => ({ ...prev, imageBottomRight: url }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Countries value</label>
                  <input value={architecture.countriesValue} onChange={onArchChange('countriesValue')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Countries label</label>
                  <input value={architecture.countriesLabel} onChange={onArchChange('countriesLabel')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Countries description</label>
                  <input value={architecture.countriesDescription} onChange={onArchChange('countriesDescription')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maritime title</label>
                  <input value={architecture.maritimeTitle} onChange={onArchChange('maritimeTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maritime description</label>
                  <input value={architecture.maritimeDescription} onChange={onArchChange('maritimeDescription')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Curated Collections Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setCollectionsOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={collectionsOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Curated Collections</div>
              <div className="text-xs text-gray-500">sectionId: <code>{collectionsSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${collectionsOpen ? 'rotate-180' : ''}`} />
          </button>

          {collectionsOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit heading + collection cards</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!collections.isActive} onChange={onCollectionsChange('isActive')} />
                  Active
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (HTML)</label>
                <input
                  value={collections.titleHtml}
                  onChange={onCollectionsChange('titleHtml')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Use <code>&lt;br /&gt;</code> for line break.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={collections.description}
                  onChange={onCollectionsChange('description')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Collection Cards</div>
                <button
                  type="button"
                  onClick={() => setCollections(prev => ({
                    ...prev,
                    items: [...(prev.items || []), { title: '', category: '', description: '', image: '' }],
                  }))}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Add Card
                </button>
              </div>

              <div className="space-y-3">
                {(collections.items || []).map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">Card #{idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => setCollections(prev => ({
                          ...prev,
                          items: (prev.items || []).filter((_, i) => i !== idx),
                        }))}
                        className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          value={item.title || ''}
                          onChange={(e) => {
                            const title = e.target.value;
                            setCollections(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, title } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category (for filtering)</label>
                        <input
                          value={item.category || ''}
                          onChange={(e) => {
                            const category = e.target.value;
                            setCollections(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, category } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Example: Fruits, Nuts, Vegetables, Seeds, Spices</p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => {
                            const description = e.target.value;
                            setCollections(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, description } : it),
                            }));
                          }}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                        <ImageDropzone
                          value={item.image || ''}
                          onChange={(url) => {
                            setCollections(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, image: url } : it),
                            }));
                          }}
                          label="Collection image"
                          previewHeight="h-48"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(collections.items || []).length === 0 && (
                  <div className="text-sm text-gray-500">No cards yet. Click “Add Card”.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Services (Our Expertise) Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setServicesOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={servicesOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Services (Our Expertise)</div>
              <div className="text-xs text-gray-500">sectionId: <code>{servicesSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
          </button>

          {servicesOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit heading + service cards</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!services.isActive} onChange={onServicesChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow</label>
                  <input
                    value={services.eyebrow}
                    onChange={onServicesChange('eyebrow')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (HTML)</label>
                  <input
                    value={services.titleHtml}
                    onChange={onServicesChange('titleHtml')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use <code>&lt;br /&gt;</code> for line break.</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Service Cards</div>
                <button
                  type="button"
                  onClick={() => setServices(prev => ({
                    ...prev,
                    items: [...(prev.items || []), { icon: 'boxes', title: '', subtitle: '', description: '', ctaText: '', ctaHref: '' }],
                  }))}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Add Card
                </button>
              </div>

              <div className="space-y-3">
                {(services.items || []).map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">Card #{idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => setServices(prev => ({
                          ...prev,
                          items: (prev.items || []).filter((_, i) => i !== idx),
                        }))}
                        className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                        <select
                          value={item.icon || 'boxes'}
                          onChange={(e) => {
                            const icon = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, icon } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="boxes">Boxes</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">(More icons can be added later.)</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          value={item.title || ''}
                          onChange={(e) => {
                            const title = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, title } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle (uppercase line)</label>
                        <input
                          value={item.subtitle || ''}
                          onChange={(e) => {
                            const subtitle = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, subtitle } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => {
                            const description = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, description } : it),
                            }));
                          }}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                        <input
                          value={item.ctaText || ''}
                          onChange={(e) => {
                            const ctaText = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, ctaText } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                        <input
                          value={item.ctaHref || ''}
                          onChange={(e) => {
                            const ctaHref = e.target.value;
                            setServices(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, ctaHref } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(services.items || []).length === 0 && (
                  <div className="text-sm text-gray-500">No cards yet. Click “Add Card”.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Testimonials (Trusted Voices) Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setTestimonialsOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={testimonialsOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Testimonials (Trusted Voices)</div>
              <div className="text-xs text-gray-500">sectionId: <code>{testimonialsSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${testimonialsOpen ? 'rotate-180' : ''}`} />
          </button>

          {testimonialsOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit section title + testimonials</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!testimonials.isActive} onChange={onTestimonialsChange('isActive')} />
                  Active
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                <input
                  value={testimonials.title}
                  onChange={onTestimonialsChange('title')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Testimonials</div>
                <button
                  type="button"
                  onClick={() => setTestimonials(prev => ({
                    ...prev,
                    items: [...(prev.items || []), { name: '', role: '', quote: '', avatar: '', rating: 5 }],
                  }))}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Add Testimonial
                </button>
              </div>

              <div className="space-y-3">
                {(testimonials.items || []).map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">Item #{idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => setTestimonials(prev => ({
                          ...prev,
                          items: (prev.items || []).filter((_, i) => i !== idx),
                        }))}
                        className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          value={item.name || ''}
                          onChange={(e) => {
                            const name = e.target.value;
                            setTestimonials(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, name } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role / Company</label>
                        <input
                          value={item.role || ''}
                          onChange={(e) => {
                            const role = e.target.value;
                            setTestimonials(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, role } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quote</label>
                        <textarea
                          value={item.quote || ''}
                          onChange={(e) => {
                            const quote = e.target.value;
                            setTestimonials(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, quote } : it),
                            }));
                          }}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-5)</label>
                        <input
                          type="number"
                          min={1}
                          max={5}
                          value={item.rating ?? 5}
                          onChange={(e) => {
                            const rating = Math.max(1, Math.min(5, Number(e.target.value || 5)));
                            setTestimonials(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, rating } : it),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Avatar</label>
                        <AvatarDropzone
                          value={item.avatar || ''}
                          onChange={(url) => {
                            setTestimonials(prev => ({
                              ...prev,
                              items: (prev.items || []).map((it, i) => i === idx ? { ...it, avatar: url } : it),
                            }));
                          }}
                          label="Avatar"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(testimonials.items || []).length === 0 && (
                  <div className="text-sm text-gray-500">No testimonials yet. Click “Add Testimonial”.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Partners Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setPartnersOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={partnersOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Partners (Marquee)</div>
              <div className="text-xs text-gray-500">sectionId: <code>{partnersSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${partnersOpen ? 'rotate-180' : ''}`} />
          </button>

          {partnersOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit title/description and partner logos</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!partners.isActive} onChange={onPartnersChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input value={partners.title} onChange={onPartnersChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input value={partners.description} onChange={onPartnersChange('description')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Logos</div>
                <button
                  type="button"
                  onClick={() => setPartners(prev => ({
                    ...prev,
                    logos: [...(prev.logos || []), { alt: 'Partner Logo', src: '' }],
                  }))}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Add Logo
                </button>
              </div>

              <div className="space-y-3">
                {(partners.logos || []).map((logo, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">Logo #{idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => setPartners(prev => ({
                          ...prev,
                          logos: (prev.logos || []).filter((_, i) => i !== idx),
                        }))}
                        className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo Upload</label>
                        <LogoDropzone
                          value={logo.src || ''}
                          onChange={(src) => {
                            setPartners(prev => ({
                              ...prev,
                              logos: (prev.logos || []).map((l, i) => i === idx ? { ...l, src } : l),
                            }));
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">Uploads are stored as a data URL for now.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Alt</label>
                        <input
                          value={logo.alt || ''}
                          onChange={(e) => {
                            const alt = e.target.value;
                            setPartners(prev => ({
                              ...prev,
                              logos: (prev.logos || []).map((l, i) => i === idx ? { ...l, alt } : l),
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(partners.logos || []).length === 0 && (
                  <div className="text-sm text-gray-500">No logos yet. Click “Add Logo”.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
